import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import { Alert, AlertTitle } from "@mui/material";
import { useStyles } from "./Modal.styles";
import React from "react";
import modalSize from "../../util/modalSizeEnum";
import { LargeActionButton } from "../Common/LargeActionButton";
import { LargeActionLink } from "../Common/LargeActionLink";
import {
	compactSecondaryModalActionStyles,
	fullScreenSecondaryModalActionStyles,
	primaryModalActionStyles,
} from "../Common/modalActionStyles";

export type ModalAlertType = "error" | "warning" | "info" | "success";
export type ModalSize = (typeof modalSize)[keyof typeof modalSize];

interface ModalWithActionBaseProps {
	isOpen: boolean;
	title?: string;
	message: string | string[];
	alertType: ModalAlertType;
	primaryButtonName: string;
	onPrimaryButtonClick: () => void;
	size?: ModalSize;
}

type SecondaryActionProps =
	| {
			secondaryButtonName: string;
			onSecondaryButtonClick: () => void;
	  }
	| {
			secondaryButtonName?: never;
			onSecondaryButtonClick?: never;
	  };

type ModalWithActionProps = ModalWithActionBaseProps & SecondaryActionProps;

export const ModalWithActionButton = ({
	isOpen,
	title,
	message,
	alertType,
	primaryButtonName,
	secondaryButtonName,
	onPrimaryButtonClick,
	onSecondaryButtonClick,
	size,
	children,
}: React.PropsWithChildren<ModalWithActionProps>) => {
	const classes = useStyles();
	const hasSecondaryAction = Boolean(secondaryButtonName && onSecondaryButtonClick);
	const secondaryActionStyles =
		size === modalSize.FULLSCREEN ? fullScreenSecondaryModalActionStyles : compactSecondaryModalActionStyles;

	const getStyle = () => {
		if (size === modalSize.MEDIUM) {
			return classes.mediumSize;
		}

		return "";
	};

	return (
		<Dialog
			fullScreen={size === modalSize.FULLSCREEN}
			classes={{ paper: getStyle() }}
			open={isOpen}
			keepMounted={false}
			disableRestoreFocus
		>
			<DialogTitle className={classes.dialogTitle}>
				<Alert severity={alertType} variant="outlined" className={classes.alert}>
					{title && <AlertTitle className={classes.alertTitle}>{title}</AlertTitle>}
				</Alert>
			</DialogTitle>
			<DialogContent className={classes.dialogContent}>
				{Array.isArray(message) ? (
					message.map((p, idx) => (
						<DialogContentText key={idx} className={classes.dialogContentMessage} paragraph>
							{p}
						</DialogContentText>
					))
				) : (
					<DialogContentText className={classes.dialogContentMessage}>{message}</DialogContentText>
				)}
				{children}
			</DialogContent>
			<DialogActions className={hasSecondaryAction ? classes.largeDialogActions : classes.dialogActions}>
				{hasSecondaryAction ? (
					<Grid
						container
						direction="column"
						justifyContent="center"
						alignItems="center"
						spacing={1}
						sx={{ width: "100%" }}
					>
						<Grid size={12} sx={{ width: "100%" }}>
							<LargeActionButton sx={primaryModalActionStyles} onClick={onPrimaryButtonClick}>
								{primaryButtonName}
							</LargeActionButton>
						</Grid>
						<Grid size={12} sx={{ width: "100%" }}>
							<LargeActionLink sx={secondaryActionStyles} onClick={onSecondaryButtonClick}>
								{secondaryButtonName}
							</LargeActionLink>
						</Grid>
					</Grid>
				) : (
					<Grid container justifyContent="flex-end" alignItems="center" spacing={2}>
						<Grid size="auto">
							<Button variant="contained" color="primary" onClick={onPrimaryButtonClick}>
								{primaryButtonName}
							</Button>
						</Grid>
					</Grid>
				)}
			</DialogActions>
		</Dialog>
	);
};
